import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChildren,
  QueryList,
  ElementRef,
  Output,
  EventEmitter
} from '@angular/core';

import {
  CdkDragDrop,
  DragDropModule
} from '@angular/cdk/drag-drop';

import {
  BehaviorSubject,
  Subject
} from 'rxjs';

import {
  PlaybackSyncEvent,
  StagePlayerState
} from '../../interfaces/playback-events';
import { Camera } from '../../interfaces/camera';
import { TimelineSegment } from '../../interfaces/timeline-segments';

@Component({
  selector: 'lib-stage',
  imports: [
    CommonModule,
    DragDropModule
  ],
  templateUrl: './stage.html',
  styleUrl: './stage.scss'
})
export class Stage implements  OnChanges {
    @Input() fullscreenContainer!: HTMLElement;
    @Input() selectedCamerasList: Camera[] = [];

    @Output() videoTimeUpdate =new EventEmitter
      <{
        cameraId: number;
        currentTime: Date;
      }>();


  playbackState =new Map
    <number, {
      isPlaying: boolean;
      playbackRate: number;
    }>();

  @Input()
  playbackSync$!: BehaviorSubject<PlaybackSyncEvent[]>;

  @ViewChildren('videoPlayer')
  videoPlayers!: QueryList<ElementRef<HTMLVideoElement>>;

  playerMap = new Map<number, StagePlayerState>();



  ngOnChanges(changes: SimpleChanges): void {
    const activeIds =
      new Set(
        this.selectedCamerasList.map(c => c.id)
      );

    for (const key of this.playerMap.keys()) {
      if (!activeIds.has(key)) {
        this.playerMap.delete(key);
      }
    }
  }

private loadPlayback(
  cameraId: number,
  url: string,
  timestamp: Date,
  segmentStart: Date,
  isPlaying: boolean,
  playbackRate: number
) {

  const cam =
    this.selectedCamerasList.find(
      c => c.id === cameraId
    );

  if (!cam) return;

  const video =
    this.getVideoElement(cameraId);

  if (!video) return;

  /**
   * IMPORTANT:
   * don't reload same source
   */
  if (video.src !== url) {
    video.pause();
    video.removeAttribute('src');
    video.load();

    requestAnimationFrame(() => {

      video.src = url;

      video.load();

      video.onloadedmetadata = async () => {

        video.currentTime = 0;

        video.playbackRate = playbackRate;

        if (isPlaying) {

        await video.play()
          .catch(console.error);

        }
        else {

        /**
         * IMPORTANT:
         * force decoder to render
         * next frame
         */
        video.muted = true;

        await video.play()
          .catch(console.error);

        requestAnimationFrame(() => {
          video.pause();
        });

      }

      };

    });

    


  }

  /**
   * existing stream
   * just sync playback state
   */
  video.playbackRate =
    playbackRate;

  this.syncPlaybackState(
    video,
    isPlaying
  );

}



  private syncPlaybackState(
    video: HTMLVideoElement,
    shouldPlay: boolean
  ) {
    if (shouldPlay && video.paused) {
      video.play().catch(console.error);
      return;
    }

    if (!shouldPlay && !video.paused) {
      video.pause();
    }
  }

  private getVideoElement(
    cameraId: number
  ): HTMLVideoElement | null {
    const index =
      this.selectedCamerasList.findIndex(
        c => c.id === cameraId
      );

    if (index === -1) return null;

    return this.videoPlayers
      ?.toArray()
      ?.[index]
      ?.nativeElement ?? null;
  }

  getGridClass(
    count: number,
    index: number
  ) {
    if (count === 1) return 'one';

    if (count === 2) return 'two-full-height';

    return '';
  }

  drop(
    event: CdkDragDrop<any[]>
  ) {
    const prev = event.previousIndex;
    const curr = event.currentIndex;

    const temp = this.selectedCamerasList[prev];

    this.selectedCamerasList[prev] =
      this.selectedCamerasList[curr];

    this.selectedCamerasList[curr] = temp;
  }

  async toggleFullscreen(
    element: HTMLElement
  ) {
    try {

    if (!document.fullscreenElement) {

      await element.requestFullscreen();

    } else {

      await document.exitFullscreen();

    }

  } catch (error) {

    console.error(
      'Fullscreen toggle failed',
      error
    );

  }
  }

downloadCurrentFrame(cameraId: number) {
  const video = this.getVideoElement(cameraId);

  if (!video) {
    return;
  }

  /**
   * ensure video actually loaded
   */
  if (
    video.readyState < 2 ||
    !video.videoWidth ||
    !video.videoHeight
  ) {
    return;
  }
  const canvas =
    document.createElement('canvas');

  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return;
  }

  ctx.drawImage(
    video,
    0,
    0,
    canvas.width,
    canvas.height
  );
  canvas.toBlob((blob) => {

    if (!blob) {
      return;
    }

    const url =
      URL.createObjectURL(blob);

    const a =
      document.createElement('a');

    a.href = url;

    a.download =
      `playback-${cameraId}-${Date.now()}.jpg`;

    document.body.appendChild(a);

    a.click();

    document.body.removeChild(a);

    URL.revokeObjectURL(url);

  }, 'image/jpeg');
}

onVideoTimeUpdate(
  cameraId: number,
  event: Event
) {

  const video =
    event.target as HTMLVideoElement;

  const player =
    this.playerMap.get(cameraId);

  if (!player?.loadedStartTime) {
    return;
  }

  const absoluteTime =
    new Date(
      player.loadedStartTime.getTime() +
      video.currentTime * 1000
    );

  this.videoTimeUpdate.emit({
    cameraId,
    currentTime: absoluteTime
  });

}

seek(
  cameraId: number,
  segment: any,
  timestamp: Date,
  isPlaying: boolean,
  playbackRate: number,
  forceReload = false
) {

  const video =
    this.getVideoElement(cameraId);

  if (!video) {
    return;
  }
const current =
  this.playerMap.get(cameraId);

const sameSegment =
  current?.segmentUrl === segment.url;

const shouldReload =
  forceReload ||
  !sameSegment ||
  !isPlaying;

const url =
  shouldReload || !current
    ? this.buildPlaybackUrl(
        segment,
        timestamp
      )
    : current!.currentUrl;


  if (shouldReload) {

    this.playerMap.set(cameraId, {
      currentUrl: url,
      segmentUrl: segment.url,
      loadedStartTime: timestamp
    });
    if (
  current?.currentUrl === url &&
  !forceReload
) {
  return;
}

    this.loadPlayback(
      cameraId,
      url,
      timestamp,
      segment.start,
      isPlaying,
      playbackRate
    );

    return;
  }

  this.syncPlaybackState(
    video,
    isPlaying
  );

}

private buildPlaybackUrl(seg: TimelineSegment, timestamp: Date): string {
  const url = new URL(seg.url);

  url.searchParams.set('start', timestamp.toISOString());

  return url.toString();
}

setPlaybackRate(rate: number) {

  const videos =
    this.videoPlayers?.toArray() ?? [];

  for (const videoRef of videos) {

    const video =
      videoRef.nativeElement;

    video.playbackRate = rate;

  }

}

enterFullScreen(){
  this.fullscreenContainer.requestFullscreen();
}

}